import { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faArrowLeft, faCircleCheck, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../assets/img/Icone_Niralakka_blanc.svg";
import loginBG from "../../assets/img/loginBG.png";
import { resetPassword } from "../../api/AUTH/auth";

const ResetPassword = () => {

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isDark, setIsDark] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Get token from URL parameters (if exists)
  const token = searchParams.get('token');
  useEffect(() => {
  if (!token) {
    toast.error("Lien invalide ou expiré");
    navigate("/");
  }
}, [token, navigate]);

  useEffect(() => {
    const isDarkMode = document.documentElement.getAttribute('data-bs-theme') === 'dark';
    setIsDark(isDarkMode);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-bs-theme') {
          setIsDark(document.documentElement.getAttribute('data-bs-theme') === 'dark');
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const validatePassword = (pwd) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumbers = /\d/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd);

    if (pwd.length < minLength) {
      return "Le mot de passe doit contenir au moins 8 caractères";
    }
    if (!hasUpperCase) {
      return "Le mot de passe doit contenir au moins une majuscule";
    }
    if (!hasLowerCase) {
      return "Le mot de passe doit contenir au moins une minuscule";
    }
    if (!hasNumbers) {
      return "Le mot de passe doit contenir au moins un chiffre";
    }
    if (!hasSpecialChar) {
      return "Le mot de passe doit contenir au moins un caractère spécial";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Réinitialiser les erreurs
    setPasswordError("");
    setConfirmPasswordError("");
    
    // Validation
    if (!password || !confirmPassword) {
      if (!password) setPasswordError("Veuillez entrer un mot de passe");
      if (!confirmPassword) setConfirmPasswordError("Veuillez confirmer votre mot de passe");
      return;
    }

    const pwd = validatePassword(password);
    if (pwd) {
      setPasswordError(pwd);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Les mots de passe ne correspondent pas");
      return;
    }

    setIsLoading(true);

    try {
      // Appel API pour réinitialiser le mot de passe
      await resetPassword(token, password);
      
      setIsSubmitted(true);
      toast.success('Mot de passe réinitialisé avec succès !');
      
      // Redirection vers la page de connexion après 2 secondes
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.error(error?.message || "Erreur lors de la réinitialisation du mot de passe");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="login-bg-blur" style={{ backgroundImage: `url(${loginBG})`, filter: 'blur(12px)' }}></div>
      <div className="login-overlay" style={{ minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", zIndex: 1 }}>
        <Container style={{ padding: '1rem' }}>
          <Row className="justify-content-center">
            <Col xl={5} lg={6} md={8} sm={10} xs={12}>
              <Card className="border-0" style={{ 
                overflow: "hidden", 
                borderRadius: "2rem",
                background: isDark ? "rgba(30, 30, 30, 0.6)" : "rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(50px)",
              }}>
                <div className="d-flex align-items-center justify-content-center" style={{
                  minHeight: "100px",
                  paddingTop: "15px",
                  paddingBottom: "10px"
                }}>
                  <img src={logo} alt="Logo" style={{ maxHeight: "80px", maxWidth: "90%", marginTop: "30px"}} />
                </div>

                <Row className="g-0">
                  <Col lg={12}>
                    <Card.Body className="login-card-body" style={{ minHeight: "auto", display: "flex", flexDirection: "column", justifyContent: "flex-start", paddingTop: "1.5rem" }}>
                      <div className="text-center mb-2">
                        <h1 className="login-welcome-title" style={{ color: 'white' }}>Réinitialiser le mot de passe</h1>
                      </div>
                      
                      {!isSubmitted ? (
                        <>
                          <div className={`text-center ${isDark ? 'login-text-muted' : 'login-text-muted-light'}`} style={{ marginBottom: "20px", color: !isDark ? 'white' : undefined }}>
                            <p className="h6">Entrez un nouveau mot de passe</p>
                          </div>

                          <Form onSubmit={handleSubmit}>
                            {/* Mot de passe */}
                            <Form.Group className="mb-3" controlId="formPassword">
                              <Form.Label className={`mb-2 ${isDark ? 'login-text-muted' : 'login-text-muted-light'}`} style={{ color: !isDark ? 'white' : undefined }}>
                                <FontAwesomeIcon icon={faLock} style={{ marginRight: '8px' }} />
                                Nouveau mot de passe
                              </Form.Label>
                              <div style={{ position: 'relative' }}>
                                <Form.Control
                                  type={showPassword ? "text" : "password"}
                                  placeholder="Entrez un nouveau mot de passe..."
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  required
                                  style={{ 
                                    borderRadius: "20px", 
                                    padding: "15px", 
                                    paddingRight: "45px",
                                    background: "rgba(255, 255, 255, 0.2)",
                                    border: `1px solid ${isDark ? 'var(--bs-gray-500)' : 'var(--bs-gray-300)'}`,
                                    color: "white"
                                  }}
                                  className="placeholder-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowPassword(!showPassword)}
                                  style={{
                                    position: 'absolute',
                                    right: '15px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: `${isDark ? 'var(--bs-gray-400)' : 'var(--bs-gray-300)'}`,
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    lineHeight: '1'
                                  }}
                                >
                                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                              </div>
                              {passwordError && (
                                <small style={{ color: '#ff4d4f', display: 'block', marginTop: '8px', fontWeight: 'bold' }}>
                                  {passwordError}
                                </small>
                              )}
                            </Form.Group>

                            {/* Confirmation mot de passe */}
                            <Form.Group className="mb-4" controlId="formConfirmPassword">
                              <Form.Label className={`mb-2 ${isDark ? 'login-text-muted' : 'login-text-muted-light'}`} style={{ color: !isDark ? 'white' : undefined }}>
                                <FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: '8px' }} />
                                Confirmer le mot de passe
                              </Form.Label>
                              <div style={{ position: 'relative' }}>
                                <Form.Control
                                  type={showConfirmPassword ? "text" : "password"}
                                  placeholder="Confirmez un mot de passe..."
                                  value={confirmPassword}
                                  onChange={(e) => setConfirmPassword(e.target.value)}
                                  required
                                  style={{ 
                                    borderRadius: "20px", 
                                    padding: "15px", 
                                    paddingRight: "45px",
                                    background: "rgba(255, 255, 255, 0.2)",
                                    border: `1px solid ${isDark ? 'var(--bs-gray-500)' : 'var(--bs-gray-300)'}`,
                                    color: "white"
                                  }}
                                  className="placeholder-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  style={{
                                    position: 'absolute',
                                    right: '15px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: `${isDark ? 'var(--bs-gray-400)' : 'var(--bs-gray-300)'}`,
                                    cursor: 'pointer',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    lineHeight: '1'
                                  }}
                                >
                                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                                </button>
                              </div>
                              {confirmPasswordError && (
                                <small style={{ color: '#ff4d4f', display: 'block', marginTop: '8px', fontWeight: 'bold' }}>
                                  {confirmPasswordError}
                                </small>
                              )}
                            </Form.Group>

                            <Button
                              type="submit"
                              variant="primary"
                              className="w-75 mx-auto d-block btn-primary"
                              disabled={isLoading}
                              style={{ borderRadius: "1rem", padding: "10px" }}
                            >
                              {isLoading ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                  />{" "}
                                  Réinitialisation...
                                </>
                              ) : (
                                "Réinitialiser le mot de passe"
                              )}
                            </Button>
                          </Form>
                        </>
                      ) : (
                        <div className="text-center">
                          <div style={{ color: '#28a745', marginBottom: '20px', fontSize: '48px' }}>
                            ✓
                          </div>
                          <h4 style={{ color: 'white', marginBottom: '10px' }}>Mot de passe réinitialisé !</h4>
                          <p className={isDark ? 'login-text-muted' : 'login-text-muted-light'} style={{ color: !isDark ? 'white' : undefined }}>
                            Vous allez être redirigé vers la page de connexion.
                          </p>
                        </div>
                      )}

                      <Button
                        variant="link"
                        onClick={() => navigate('/')}
                        style={{
                          color: 'white',
                          textDecoration: 'none',
                          fontSize: '14px',
                          marginTop: '20px'
                        }}
                        className="p-0 d-flex align-items-center justify-content-center"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '8px' }} />
                        Retour à la connexion
                      </Button>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ResetPassword;
